import { useOrganization } from "@clerk/expo";
import { useEffect, useMemo } from "react";

import { User } from "@/types/resources";

export type UsersMap = Record<string, User>;

const formatMemberName = (user: User, abbreviated = false): string => {
  const first = user.firstName ?? "";
  const last = user.lastName ?? "";
  if (!first && !last) return "";
  if (abbreviated && last) return `${first} ${last[0]}.`.trim();
  return `${first} ${last}`.trim();
};

/**
 * Returns org users map and a formatMemberName helper.
 * Falls back to an empty map when there is no org context.
 */
const useOrganizationMembers = () => {
  const { memberships } = useOrganization({
    memberships: {
      infinite: true,
      pageSize: 500,
    },
  });

  useEffect(() => {
    if (memberships?.hasNextPage) {
      void memberships.fetchNext();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberships?.hasNextPage, memberships?.fetchNext]);

  const membershipsData = memberships?.data;

  const users: UsersMap = useMemo(() => {
    if (!membershipsData) return {};
    return Object.fromEntries(
      membershipsData.map((m) => [
        m.publicUserData?.userId,
        {
          emailAddress: m.publicUserData?.identifier ?? null,
          firstName: m.publicUserData?.firstName ?? null,
          id: m.publicUserData?.userId ?? "",
          imageUrl: m.publicUserData?.imageUrl ?? "",
          lastName: m.publicUserData?.lastName ?? null,
        },
      ])
    );
  }, [membershipsData]);

  return { formatMemberName, users };
};

export default useOrganizationMembers;
